import { useEffect } from 'react';
import { useLocation } from 'react-router';
import { AUTH_CALLBACK_PATH, config } from '../config';
import { useAppSelector } from '../store/hooks';

// gtag's globals, declared here rather than pulling in @types/gtag.js for two
// symbols. Augmenting Window needs interface declaration merging, which a type
// alias cannot do — hence the disable below, this not being a .d.ts.
declare global {
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

// The DOM is the guard, not a module-level flag: a flag survives a hot reload or
// a test re-render and then never lets a second instance load.
const loadGtag = (measurementId: string): void => {
  if (document.head.querySelector('script[data-ga-loader]') !== null) {
    return;
  }

  const script = document.createElement('script');
  script.setAttribute('data-ga-loader', '');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer ?? [];
  // Must push an Arguments object: gtag.js reads a pushed Array as an ordinary
  // data-layer message and silently ignores it.
  window.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer?.push(arguments);
  };

  window.gtag('js', new Date());
  // Off because the route-change effect below owns every page_view. GA's default
  // double-counts the first route — once here and once from the effect.
  window.gtag('config', measurementId, { send_page_view: false });
};

// One definition of "is analytics allowed right now", and **it checks the auth
// status itself rather than trusting its call site.** ProtectedLayout runs its
// hooks as soon as the route matches — before RequireAuth has decided what to
// render — so trusting the call site fires analytics for an unauthenticated
// visit that immediately bounces to /login.
export const useAnalyticsEnabled = (): boolean => {
  const status = useAppSelector((state) => state.auth.status);
  return config.analytics.measurementId !== '' && status === 'authenticated';
};

export const useAnalytics = (): void => {
  const { pathname } = useLocation();
  const enabled = useAnalyticsEnabled();
  const { measurementId } = config.analytics;

  useEffect(() => {
    if (!enabled) {
      return;
    }
    loadGtag(measurementId);
  }, [enabled, measurementId]);

  useEffect(() => {
    if (!enabled || pathname === AUTH_CALLBACK_PATH || window.gtag === undefined) {
      return;
    }
    // origin + pathname, never `href` or `search`: the callback route carries a
    // live OAuth code and state in its query string.
    window.gtag('event', 'page_view', {
      page_path: pathname,
      page_location: `${window.location.origin}${pathname}`,
    });
  }, [enabled, pathname]);
};
